/**
 * Vitrine (Casa) — scrollytelling landing page.
 *
 * Orchestrates scenes in a deliberate order so the page reads like a
 * documentary rather than a brochure:
 *
 *   Hero        →  First breath. Cover photo, name, location.
 *   LiveBand    →  Grounding truth. Real progress, current stage, delivery.
 *   Approach    →  Chapter break. Word-by-word pull quote.
 *   Exploded    →  Signature moment. SVG house unstacks on scroll.
 *   Timeline    →  The path. Horizontal sweep through milestones.
 *   Gallery     →  Evidence. Curated photo stack with parallax.
 *   Specs       →  Ficha técnica. The practical numbers.
 *   LeadForm    →  The ask. Access to the full document package.
 *   Footer      →  Sign-off.
 */

import { useEffect, useMemo, useState } from 'react';
import type { ProjectSpecs, PublicShowcase } from './lib/api';
import {
  fetchProjectSpecs,
  fetchShowcase,
  slugFromHostname,
} from './lib/api';
import { Hero } from './scenes/Hero';
import { LiveBand } from './scenes/LiveBand';
import { Approach } from './scenes/Approach';
import { HouseFront } from './scenes/HouseFront';
import { HouseBack } from './scenes/HouseBack';
import { HouseExploded } from './scenes/HouseExploded';
import { LittlestGenese } from './scenes/LittlestGenese';
import { Condo } from './scenes/Condo';
// Wave 78 F1 · Route + Timeline foram substituídos por MapaInterativo
// (Google Maps JS real, zoom/pan/street view) e CronogramaVivo (puxa
// work_stages direto, sem fallback hardcoded). Route.tsx e Timeline.tsx
// permanecem no repo só pra histórico — não são mais importados.
import { MapaInterativo } from './scenes/MapaInterativo';
import { CronogramaVivo } from './scenes/CronogramaVivo';
import { Investimento } from './scenes/Investimento';
import { Gallery } from './scenes/Gallery';
import { Specs } from './scenes/Specs';
import { MemorialFlipbook } from './scenes/MemorialFlipbook';
import { Credits } from './scenes/Credits';
import { LeadForm } from './scenes/LeadForm';
import { Footer } from './scenes/Footer';
import { PublicHousy } from './scenes/PublicHousy';
import { LocaleToggle } from './scenes/LocaleToggle';
import { ContactCTA, StickyMobileCTA } from './scenes/ContactCTA';
import { applyShowcaseSeo } from './lib/seo';

export function App() {
  const slug = useMemo(() => slugFromHostname(window.location.hostname), []);
  const [data, setData] = useState<PublicShowcase | null>(null);
  const [specs, setSpecs] = useState<ProjectSpecs>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetchShowcase(slug)
      .then((d) => {
        if (!live) return;
        if (d === 'not_found') setError('not_found');
        else setData(d);
      })
      .catch((e) => {
        if (live) setError(String(e?.message ?? 'unknown'));
      });
    // Specs fetched in parallel — render falls back to hardcoded values
    // while these are loading or if the request fails (see specValue
    // and specJson helpers in lib/api). No reason to block the page on
    // them.
    fetchProjectSpecs(slug)
      .then((s) => {
        if (live) setSpecs(s);
      })
      .catch(() => {
        // Non-fatal — specs missing means render uses defaults.
      });
    return () => {
      live = false;
    };
  }, [slug]);

  // Wave W128.6 · runtime SEO meta-tags. SPA sem SSR — crawlers que
  // executam JS (Google, alguns scrapers do Twitter/LinkedIn) pegam o
  // <title> + og:* atualizados. Para clients sem JS (WhatsApp preview,
  // Telegram) o fallback em index.html ainda funciona.
  useEffect(() => {
    if (data) applyShowcaseSeo(data);
  }, [data]);

  if (error === 'not_found') return <NotFound slug={slug} />;
  if (!data) return <Loading />;

  return (
    <main className="relative min-h-screen bg-bg text-ink">
      <Hero data={data} />
      <LiveBand data={data} />
      <HouseFront />
      <Approach />
      {/* Wave 44.10 · both variants stay. Two ways of looking at the
          same house: the line drawing shows it as projected — technical
          discipline behind the surfaces. The material render shows it
          as built — light, wood, stone, glass. Monograph-style
          dividers.

          Wave 299b · order swapped: line first (the drawing exists
          before the building does), then materials (how the projection
          becomes a place). Reads more like an architect's monograph
          and lets the eye warm up on the simpler diagram before the
          richer scene lands. */}
      <VariantDivider
        label="Em linha"
        hint="Como a casa foi projetada. Uma espessura só, nada de textura — o diagrama que sustenta o objeto."
      />
      <HouseExploded variant="oneliner" />
      <VariantDivider
        label="Em matéria"
        hint="Como a casa se apresenta. Luz do fim de tarde, madeira, pedra, vidro."
      />
      <HouseExploded variant="materials" specs={specs} />
      {/* Wave 317 · Littlest Gênese — terceiro variant, "Em vida".
          Cena composta de blocos autorais (lego), com luz quente, OrbitControls
          e ambient motion (suítes pulsando ao entardecer, fumaça da chaminé,
          árvores balançando, água brilhando). Premissa: encantar o comprador. */}
      <VariantDivider
        label="Em vida"
        hint="Como a casa se sente. Sol baixo, luzes acendendo, vida dentro — explorável."
      />
      <LittlestGenese specs={specs} />
      <HouseBack />
      <Condo />
      {/* Wave 78 F1 SUB 1 — mapa real Google Maps (zoom, pan, street view).
          Substitui Route.tsx + NinhoVerdeMap.tsx (iframe legado sem chave,
          pointer-events:none, GPS hardcoded). */}
      <MapaInterativo data={data} />
      {/* Wave 78 F1 SUB 2 — cronograma vivo puxado de work_stages via
          showcase.service.ts (com fallback silencioso showcase_milestones
          → work_stages quando o primeiro tá vazio). Substitui Timeline.tsx
          (que mostrava 7 etapas hardcoded vazias). */}
      <CronogramaVivo milestones={data.milestones} />
      <Gallery photos={data.heroPhotos} />
      <Specs data={data} specs={specs} />
      {/* Wave 78 F1 SUB 3 — módulo Investimento. Público vê "a partir de
          R$ X" (D1 JP); banda P10-P90 fica gated PM-only (F3). Posicionado
          entre Specs e Memorial pra fluir do dado prático pra ficha
          técnica completa. */}
      <Investimento data={data} />
      {/* Wave W128.6 · ContactCTA — banda dedicada de contato (WhatsApp
          deeplink + lead form). Posicionada depois de Investimento porque
          é o ponto natural do buyer ter dúvida concreta ("e o preço?
          como falo?"). Em vez de obrigá-lo a scrollar até #lead, oferece
          WhatsApp imediato. */}
      <ContactCTA data={data} />
      {/* Wave 337 · Memorial flipbook. After the ficha técnica
          (Specs) the page slows down and the visitor is invited to
          *read* the project. The flipbook turns project_specs into
          chapters — identidade, condomínio, dimensões, etc. — and
          falls back to a vertical card stack on mobile. */}
      <MemorialFlipbook data={data} specs={specs} />
      <Credits />
      <LeadForm slug={data.slug} />
      <Footer />
      {/* Wave 70 · public Housy chat for visitors. Floats over the
          page content; locked to public-only data on the server. */}
      <PublicHousy slug={data.slug} />
      {/* Wave 78 · floating EN/PT toggle, top-right. */}
      <LocaleToggle />
      {/* Wave W128.6 · sticky mobile-only CTA bar. Aparece depois do
          primeiro fold e some quando LeadForm está em vista. md+ hidden. */}
      <StickyMobileCTA data={data} />
    </main>
  );
}

function Loading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg text-ink-subtle">
      <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.2em]">
        <span className="inline-block h-[1px] w-8 animate-pulse bg-accent" />
        Carregando
      </div>
    </div>
  );
}

/**
 * Minimal full-width divider that flags which render variant is about
 * to play. Kept intentionally quiet so the cinematic scene underneath
 * remains the protagonist — the divider is navigation, not spectacle.
 * Temporary while Wave 44.2's A/B decision is open.
 */
function VariantDivider({ label, hint }: { label: string; hint: string }) {
  return (
    <section className="relative border-t border-line bg-bg py-16 sm:py-20">
      <div className="mx-auto max-w-4xl px-6 text-center sm:px-12">
        <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-accent">
          {label}
        </p>
        <p className="mx-auto mt-4 max-w-[40ch] text-sm leading-relaxed text-ink-muted sm:text-base">
          {hint}
        </p>
      </div>
    </section>
  );
}

function NotFound({ slug }: { slug: string }) {
  return (
    <main className="relative flex min-h-screen items-center justify-center bg-bg px-6 text-center">
      <div className="max-w-md">
        <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-accent">
          Vitrine
        </p>
        <h1 className="mt-5 font-display text-5xl font-light leading-[0.95] tracking-tightest text-ink">
          Casa não encontrada.
        </h1>
        <p className="mt-6 text-sm text-ink-muted">
          Não há nenhuma obra publicada em{' '}
          <span className="text-ink">{slug}.nullscience.ai</span>{' '}
          no momento.
        </p>
      </div>
    </main>
  );
}
